// @flow

import { sumBy } from 'lodash';

import type { DetailResult } from './types';
import {
  crustPrices,
  ingredientIcons,
  meatPrices,
  ingredientNames,
  saucePrices,
  seafoodPrices,
  tasteFitTable,
  vegetablePrices,
} from './constants';
import { impossible } from './utils';

const Crusts: Crust[] = Object.keys(crustPrices);
const Sauces: Sauce[] = Object.keys(saucePrices);

type Vegetable = $Keys<typeof vegetablePrices>;
type Meat = $Keys<typeof meatPrices>;
type Seafood = $Keys<typeof seafoodPrices>;

export type Topping =
  | { type: 'vegetable', topping: Vegetable }
  | { type: 'meat', topping: Meat }
  | { type: 'seafood', topping: Seafood };
export type Crust = $Keys<typeof crustPrices>;
export type Sauce = $Keys<typeof saucePrices>;
export type Ingredient = Crust | Sauce | Topping;

const makeVegetableTopping = (vegetable: Vegetable): Topping => ({ type: 'vegetable', topping: vegetable });
const makeMeatTopping = (meat: Meat): Topping => ({ type: 'meat', topping: meat });
const makeSeafoodTopping = (seafood: Seafood): Topping => ({ type: 'seafood', topping: seafood });

const vegetableToppings = Object.keys(vegetablePrices).reduce((acc, k) => {
  acc[k] = makeVegetableTopping(k);
  return acc;
}, {});
const meatToppings = Object.keys(meatPrices).reduce((acc, k) => {
  acc[k] = makeMeatTopping(k);
  return acc;
}, {});
const seafoodToppings = Object.keys(seafoodPrices).reduce((acc, k) => {
  acc[k] = makeSeafoodTopping(k);
  return acc;
}, {});
const Toppings: { [$Keys<typeof ingredientIcon.toppings>]: Topping } = {
  ...vegetableToppings,
  ...meatToppings,
  ...seafoodToppings,
};

const ingredientIcon = (ingredient: Ingredient): string => {
  if (typeof ingredient === 'string') {
    return ingredientIcons[ingredient];
  } else {
    return ingredientIcons.toppings[ingredient.topping];
  }
};

const ingredientName = (ingredient: Ingredient): string => {
  if (typeof ingredient === 'string') {
    return ingredientNames[ingredient];
  } else {
    return ingredientNames.toppings[ingredient.topping];
  }
};

const crustPrice = (crust: Crust): number => {
  return crustPrices[crust];
};

const saucePrice = (sauce: Sauce): number => {
  return saucePrices[sauce];
};

const toppingPrice = (topping: Topping): number => {
  switch (topping.type) {
    case 'vegetable':
      return vegetablePrices[topping.topping];
    case 'meat':
      return meatPrices[topping.topping];
    case 'seafood':
      return seafoodPrices[topping.topping];
    default:
      return impossible(topping.type);
  }
};

class Pizza {
  crust: Crust;
  sauce: Sauce;
  toppings: Topping[];

  constructor(crust: Crust, sauce: Sauce, toppings: Topping[]) {
    this.crust = crust;
    this.sauce = sauce;
    this.toppings = toppings;
  }

  get basePrice(): number {
    return crustPrice(this.crust) + saucePrice(this.sauce) + sumBy(this.toppings, toppingPrice);
  }

  get tasteFitScore(): number {
    const currentSauceFitTable = tasteFitTable[this.sauce];
    let score = 0;
    for (const toppingName in currentSauceFitTable) {
      if (this.hasTopping(toppingName)) {
        score += currentSauceFitTable[toppingName];
      }
    }
    return score;
  }

  tasteFitScoreDetail(): DetailResult {
    return [[this.sauce, this.tasteFitScore]];
  }

  hasTopping(name: string): boolean {
    return this.toppings.some(topping => topping.topping === name);
  }
}

export { Crusts, Sauces, Toppings, Pizza, ingredientName, ingredientIcon };
